import type { PortfolioPublished, PortfoliosView } from "@buildpy/core/views/g4a/portfolios";
import { Plus } from "lucide-react";
import { Link } from "@/lib/nav";

import FilterChips from "@/components/filter-chips";
import ListSearch from "@/components/list-search";
import Pager from "@/components/pager";
import { Button } from "@buildpy/ui/button";
import { listHref } from "@buildpy/core/modules/list/search";
import PortfolioGrid from "@/modules/portfolio/ui/portfolio-grid";
import PublicPageLink from "@/modules/portfolio/ui/public-page-link";
import PublicPageLinks from "@/modules/public-page/ui/public-page-links";
import PageHeader from "@/shell/page-header";

type Published = PortfolioPublished;

// 시공사례 목록 — 표는 검색·공개 칩·쪽을 건 `search` 결과, 전체 목록은 공개 링크·드래그 정렬(전체 순서)에 쓴다.
const Page = ({ data }: { data: PortfoliosView }) => {
  const { q, published, publicUrl, links, portfolios, rows, allIds } = data;
  const result = { total: data.total, page: data.page, pageSize: data.pageSize, pageCount: data.pageCount, counts: data.counts };

  // 칩·쪽 링크는 검색어를 싣고 간다 — 칩을 누르면 쪽은 1로.
  const href = (p: Published | null, page?: number) => listHref("/app/portfolio", { q, published: p, page });
  const chips = [
    { href: href(null), label: "전체", count: result.counts.ALL },
    { href: href("public"), label: "공개", count: result.counts.PUBLIC },
    { href: href("private"), label: "비공개", count: result.counts.PRIVATE },
  ];

  return (
    <>
      <PageHeader
        actions={
          <>
            {publicUrl && (
              <PublicPageLink
                enabled={data.publicEnabled}
                publishedCount={data.publishedCount}
                url={publicUrl}
              />
            )}
            <Button asChild size="sm">
              <Link href="/app/portfolio/new">
                <Plus className="size-4" />
                사례
              </Link>
            </Button>
          </>
        }
        description="상담 자리에서 보여 주는 갤러리입니다. 공개로 둔 것만 고객 화면에 실립니다."
        meta={`${result.total}건`}
        title="시공사례"
      />

      <PublicPageLinks
        canEdit={data.canEdit}
        links={links}
        portfolios={portfolios}
        publicEnabled={data.publicEnabled}
      />

      {portfolios.length > 0 && (
        <div className="flex flex-col gap-y-3">
          <ListSearch
            action="/app/portfolio"
            aria-label="시공사례 검색"
            keep={{ published }}
            placeholder="제목 · 한 줄 소개"
            q={q}
          />
          <FilterChips activeHref={href(published)} aria-label="공개 여부" items={chips} />
        </div>
      )}

      {/* 쪽·조건이 바뀌면 끌어 둔 순서 상태를 버리고 새로 그린다. */}
      <PortfolioGrid
        key={href(published, result.page)}
        allIds={allIds}
        canReorder={!q && !published}
        hasAny={portfolios.length > 0}
        offset={(result.page - 1) * result.pageSize}
        rows={rows}
      />

      <Pager hrefFor={(page) => href(published, page)} page={result.page} pageCount={result.pageCount} />
    </>
  );
};

export default Page;
